import {getHttpClient} from '../modules/httpClient.js';
import {Notification} from '../modules/notification.js';
import {ensureTurnstileLoaded, renderTurnstile} from '../modules/turnstile.js';
import {t} from '../localization/translate.js';

/**
 * ReportButton class handles the submission of job posting reports.
 * Uses a shared dialog that gets populated with the specific job posting data when opened.
 */
export class ReportButton {
    #button;
    #jobPostingId;
    static #submitHandlerAttached = false;

    constructor(button) {
        this.#button = button;
        this.#jobPostingId = button.dataset.jobPostingId;

        if (!this.#jobPostingId) {
            console.warn('ReportButton: Job Posting ID is missing.');
            return;
        }

        this.#attachButtonEventListeners();
    }

    static #ensureSubmitHandlerAttached() {
        if (ReportButton.#submitHandlerAttached) {
            return;
        }

        const dialog = document.getElementById('report-job-posting-dialog');
        const form = dialog.querySelector('form');

        form.addEventListener('submit', (event) => ReportButton.#handleSubmit(event));

        ReportButton.#submitHandlerAttached = true;
    }

    /**
     * Mark all report buttons for a specific job posting as reported/not reported
     * @param {string} jobPostingId - The job posting UUID
     * @param {boolean} hasReported - Whether the job has been reported
     */
    static markAsReported(jobPostingId, hasReported) {
        const reportButtons = document.querySelectorAll(`.js-reportButton[data-job-posting-id="${jobPostingId}"]`);

        reportButtons.forEach(button => {
            button.dataset.hasReported = hasReported.toString();
            button.disabled = hasReported;
            button.setAttribute('aria-label', hasReported ? t('Already reported') : t('Report this job posting'));

            button.classList.add('text-gray-300');
        });
    }

    #attachButtonEventListeners() {
        this.#button.addEventListener('click', () => this.#openDialog());
    }

    #openDialog() {
        // Read current state from DOM (source of truth)
        const hasReported = this.#button.dataset.hasReported === 'true';

        if (hasReported) {
            return;
        }

        // Get the dialog
        const dialog = document.getElementById('report-job-posting-dialog');
        if (!dialog) {
            console.warn('ReportButton: dialog not found in DOM.');
            return;
        }

        // Get the form from within the dialog
        const form = dialog.querySelector('form');
        const submitButton = form.querySelector('.js-submitReport');

        // Ensure submit handler is attached
        ReportButton.#ensureSubmitHandlerAttached();

        // Reset the form
        form.reset();

        // Reset container visibility (will be shown when user selects a reason)
        const descriptionContainer = dialog.querySelector('.js-reasonDescriptionContainer');
        if (descriptionContainer) {
            descriptionContainer.classList.add('hidden');
        }

        // Populate the shared dialog with this job posting's ID
        const jobPostingIdInput = document.getElementById('report-job-posting-id');
        if (!jobPostingIdInput) {
            console.warn('ReportButton: Job Posting ID hidden field not found in DOM.');
            return;
        }
        jobPostingIdInput.value = this.#jobPostingId;

        // Disable submit button initially
        submitButton.disabled = true;

        // Enable submit button when a reason is selected
        const reasonInputs = form.querySelectorAll('input[name="reason"]');
        reasonInputs.forEach((input) => {
            input.addEventListener(
                'change',
                () => {
                    submitButton.disabled = false;
                },
                {once: true}
            ); // Use once: true to avoid duplicate listeners
        });

        // Open the dialog
        dialog.showModal();

        // Guest-only: Turnstile is rendered inside the shared dialog, but we lazy-load its scripts so it doesn't slow down
        // the page for everyone (and for users who never open the dialog).
        const turnstileElement = dialog.querySelector('.cf-turnstile');
        if (turnstileElement) {
            ensureTurnstileLoaded()
                .then(() => {
                    // Turnstile may auto-render widgets on load; schedule our manual render one tick later
                    // and only render if it didn't already render.
                    window.setTimeout(() => renderTurnstile(turnstileElement), 0);
                })
                .catch((error) => {
                    Notification.error(t('Captcha failed to load: :message', {message: error.message}));
                });
        }
    }

    static async #handleSubmit(event) {
        event.preventDefault();

        const dialog = document.getElementById('report-job-posting-dialog');
        if (!dialog) {
            Notification.error(t('Report dialog not found.'));
            return;
        }

        const form = dialog.querySelector('form');
        if (!(form instanceof HTMLFormElement)) {
            Notification.error(t('Report form not found.'));
            return;
        }

        const submitButton = dialog.querySelector('.js-submitReport');
        if (!(submitButton instanceof HTMLButtonElement)) {
            Notification.error(t('Report submit button not found.'));
            return;
        }

        const formData = new FormData(form);
        const reason = formData.get('reason');
        const jobPostingId = formData.get('job_posting_id');

        if (!jobPostingId) {
            Notification.error(t('Job posting ID is missing.'));
            return;
        }

        let shouldCloseDialog = false;

        try {
            submitButton.disabled = true;

            const httpClient = getHttpClient();
            const payload = {
                reason: reason,
                reason_description: formData.get('reason_description') || '',
            };

            // Check if guest fields are present (only rendered for guests)
            const guestName = formData.get('guest_name');
            const guestEmail = formData.get('guest_email');
            const isGuest = guestName !== null;

            if (isGuest) {
                payload.guest_name = guestName;
                payload.guest_email = guestEmail;
                payload['cf-turnstile-response'] = formData.get('cf-turnstile-response');
            }

            if (isGuest && !payload['cf-turnstile-response']) {
                Notification.error(t('Please complete the captcha before submitting.'));
                return;
            }

            const endpoint = isGuest
                ? `/jobs/postings/${jobPostingId}/report/guest`
                : `/jobs/postings/${jobPostingId}/report`;

            await httpClient.post(endpoint, payload);

            ReportButton.markAsReported(jobPostingId, true);

            Notification.success(t('Thank you for your report. We will review it shortly.'));
            shouldCloseDialog = true;
        } catch (error) {
            if (error.status === 409) {
                ReportButton.markAsReported(jobPostingId, true);

                Notification.info(t('You have already reported this job posting.'));
                shouldCloseDialog = true;
            } else {
                Notification.error(t('Error submitting report: :message', {message: error.message}));
            }
        } finally {
            submitButton.disabled = false;
            if (shouldCloseDialog) {
                dialog.close();
            }
        }
    }
}
